/**
 * AI v1 (aiv1) — shared contract for the scoring prompt, the model JSON,
 * and the public result. The server owns the queue; this module owns the
 * bytes that have to stay identical across every call so a prompt cache hits.
 *
 * The fixed prefix is built once from the rubric constants. It must not
 * contain a timestamp, an id, or anything that changes per recording.
 */
import { z } from "zod";
import { MARK_TYPES, type Domain, type MarkType, type MaterialChar } from "./types.js";
import { RUBRIC_DIMENSIONS, RUBRIC_WEIGHTS, SCORE_ANCHORS } from "./rubric.js";
import { charScore, READING_WEIGHTS, round4 } from "./tristate.js";

export const AI_VERSION = "aiv1" as const;
export const AI_PROMPT_VERSION = "aiv1.prompt.1" as const;
export const AI_NOTICE = "本版AI结果仅供参考" as const;
export const AI_MODEL_DEFAULT = "mimo-v2.6-flash";
export const AI_ASR_MODEL = "mimo-v2.5-asr";
export const AI_MAX_OUTPUT_TOKENS = 4096;
/** DSH allows 180s idle and 300s overall. Client timeouts are not retried. */
export const AI_TIMEOUT_MS = 240_000;
export const AI_ASR_TIMEOUT_MS = 30_000;
/** Recordings longer than this are stored as `skipped` and never sent. */
export const AI_MAX_DURATION_MS = 45_000;

/**
 * Why a scoring call failed. Stored on the row; the student card still says
 * 「评分失败」. `null` means the failure is not one of these (未配置, 录音不存在).
 */
export const AI_ERROR_TYPES = [
  "timeout",
  "network",
  "http_4xx",
  "http_5xx",
  "rate_limited",
  "model_output",
  "audio_read",
] as const;
export type AiErrorType = (typeof AI_ERROR_TYPES)[number];

/**
 * Sleep before each transient retry, while the row stays `running`.
 * Three retries means four calls. Client timeouts are not in this list:
 * DSH keeps billing the abandoned call.
 */
export const AI_TRANSIENT_BACKOFF_MS = [15_000, 60_000, 240_000] as const;
export const AI_TRANSIENT_RETRIES = 3;

export function isTransientAiError(type: AiErrorType | null | undefined): boolean {
  return type === "network" || type === "http_5xx" || type === "rate_limited";
}

export const AI_STATUSES = ["queued", "running", "done", "failed", "skipped"] as const;
export type AiStatus = (typeof AI_STATUSES)[number];

/** `dsh` is the default. `mimo-direct` is an off-by-default fallback. */
export const AI_AUDIO_PROVIDERS = ["dsh", "mimo-direct"] as const;
export type AiAudioProvider = (typeof AI_AUDIO_PROVIDERS)[number];
export const AI_AUDIO_PROVIDER_DEFAULT: AiAudioProvider = "dsh";

export const AI_SKIP_TOO_LONG = "录音超过 45 秒，不送评分";
export const AI_SKIP_QC = "质检未通过，不送评分";

const ANCHORS = [0, 0.25, 0.5, 0.75, 1] as const;
export type ScoreAnchor = (typeof ANCHORS)[number];

/** Nearest rubric anchor. A midpoint rounds toward the higher anchor. */
export function snapToAnchor(value: number): ScoreAnchor {
  let best: ScoreAnchor = 0;
  let bestDist = Infinity;
  for (const anchor of ANCHORS) {
    const dist = Math.abs(value - anchor);
    if (dist < bestDist - 1e-9 || (Math.abs(dist - bestDist) <= 1e-9 && anchor > best)) {
      best = anchor;
      bestDist = dist;
    }
  }
  return best;
}

export function anchorLabel(value: number): string {
  const snapped = snapToAnchor(value);
  const found = SCORE_ANCHORS.find((a) => a.value === snapped);
  return found ? found.label : String(snapped);
}

/**
 * JSON with sorted keys, no whitespace. Used for every JSON blob embedded in
 * the fixed prefix so the bytes do not depend on object insertion order.
 */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(sortKeys(value));
}

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value && typeof value === "object") {
    const src = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(src).sort()) out[key] = sortKeys(src[key]);
    return out;
  }
  return value;
}

const READING_UTTERANCE_KEYS = ["accuracy", "completeness", "fluency", "prosodic"] as const;
const SINGING_UTTERANCE_KEYS = ["intelligibility", "rhythm"] as const;

function rubricLines(domain: "reading" | "singing"): string {
  return RUBRIC_DIMENSIONS[domain]
    .map(([key, label, detail]) => `- ${key} ${label}：${detail}`)
    .join("\n");
}

const FEW_SHOT_READING = {
  chars: [
    {
      char_index: 0,
      initial: 1,
      final: 1,
      tone: 1,
      onset: null,
      nucleus: null,
      coda: null,
      mark_type: "correct",
      note: "声母清楚",
    },
    {
      char_index: 1,
      initial: 0.75,
      final: 1,
      tone: 0.5,
      onset: null,
      nucleus: null,
      coda: null,
      mark_type: "accented",
      note: "阳平不够稳",
    },
  ],
  utterance: { accuracy: 0.75, completeness: 1, fluency: 0.75, prosodic: 0.5 },
  summary: "声母清楚，阳平略飘。",
};

const FEW_SHOT_SINGING = {
  chars: [
    {
      char_index: 0,
      initial: null,
      final: null,
      tone: null,
      onset: 0.75,
      nucleus: 1,
      coda: 0.5,
      mark_type: "accented",
      note: "字尾收得早",
    },
    {
      char_index: 1,
      initial: null,
      final: null,
      tone: null,
      onset: 1,
      nucleus: 1,
      coda: 1,
      mark_type: "correct",
      note: "",
    },
  ],
  utterance: { intelligibility: 0.75, rhythm: 0.5 },
  summary: "字腹稳定，节奏略赶。",
};

const OUTPUT_SCHEMA = {
  type: "object",
  additionalProperties: false,
  required: ["chars", "utterance", "summary"],
  properties: {
    chars: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["char_index", "mark_type", "note"],
        properties: {
          char_index: { type: "integer", minimum: 0 },
          initial: { type: ["number", "null"], minimum: 0, maximum: 1 },
          final: { type: ["number", "null"], minimum: 0, maximum: 1 },
          tone: { type: ["number", "null"], minimum: 0, maximum: 1 },
          onset: { type: ["number", "null"], minimum: 0, maximum: 1 },
          nucleus: { type: ["number", "null"], minimum: 0, maximum: 1 },
          coda: { type: ["number", "null"], minimum: 0, maximum: 1 },
          mark_type: { enum: [...MARK_TYPES] },
          note: { type: "string" },
        },
      },
    },
    utterance: {
      type: "object",
      properties: {
        accuracy: { type: "number", enum: [...ANCHORS] },
        completeness: { type: "number", enum: [...ANCHORS] },
        fluency: { type: "number", enum: [...ANCHORS] },
        prosodic: { type: "number", enum: [...ANCHORS] },
        intelligibility: { type: "number", enum: [...ANCHORS] },
        rhythm: { type: "number", enum: [...ANCHORS] },
      },
    },
    summary: { type: "string" },
  },
};

/**
 * System prompt + both rubrics + JSON schema + few-shot examples.
 * Byte-identical for every scoring call. Do not append dates, ids, or
 * the current material — those belong in {@link buildAiMaterialBlock}.
 */
export function buildFixedPrefix(): string {
  return [
    "你是星海正音的发音评分员。对照用户消息中的参考文本和锁定拼音，按下面的评分表给这一条朗读或演唱打分。",
    "只输出一个 JSON 对象，不要 Markdown，不要解释，不要输出总分（服务端按权重计算）。",
    "思考过程保持开启。不要在输出里写日期、录音编号或说话人。",
    "",
    "# 锚点",
    "语句维度只能是这些锚点之一：",
    canonicalJson(SCORE_ANCHORS),
    "",
    "# 评分表",
    "朗读维度：",
    rubricLines("reading"),
    "朗读权重：",
    canonicalJson(RUBRIC_WEIGHTS.readingChar),
    canonicalJson(RUBRIC_WEIGHTS.readingUtterance),
    "演唱维度：",
    rubricLines("singing"),
    "演唱权重：",
    canonicalJson(RUBRIC_WEIGHTS.singingChar),
    canonicalJson(RUBRIC_WEIGHTS.singingUtterance),
    "朗读逐字打 initial、final、tone，onset、nucleus、coda 填 null。",
    "演唱逐字打 onset、nucleus、coda，initial、final、tone 填 null。演唱不评声调。",
    "mark_type 只能是 correct、substitution、deletion、insertion、accented、unrecognizable。",
    "note 是给这条字的简短中文标注。summary 是一两句中文。",
    "",
    "# 输出 JSON Schema",
    canonicalJson(OUTPUT_SCHEMA),
    "",
    "# 示例（虚构，不是当前材料）",
    "朗读示例（音节：妈、麻）：",
    canonicalJson(FEW_SHOT_READING),
    "演唱示例（音节：月、亮）：",
    canonicalJson(FEW_SHOT_SINGING),
  ].join("\n");
}

export const AI_FIXED_PREFIX = buildFixedPrefix();

/** Short fixed instruction for the optional ASR pass. Also cache-stable. */
export const AI_ASR_PREFIX =
  "把这段普通话录音转写成汉字。只输出听到的字，不要评分，不要解释，不要加标点以外的说明。";

export interface AiPromptChar {
  char: string;
  pinyin_locked: string;
  initial: string | null;
  final: string | null;
  tone: number | null;
}

/**
 * Per-recording text. This is the variable part and always comes after the
 * fixed prefix. It must not be prepended to the system prompt.
 */
export function buildAiMaterialBlock(input: {
  domain: Domain;
  text: string;
  chars: readonly AiPromptChar[];
  asrText?: string | null;
  retry?: boolean;
}): string {
  const reading = input.domain !== "singing";
  const chars = input.chars.map((c, index) => ({
    char_index: index,
    char: c.char,
    pinyin_locked: c.pinyin_locked,
    initial: c.initial,
    final: c.final,
    tone: c.tone,
  }));
  const lines = [
    reading ? "领域：朗读" : "领域：演唱",
    reading
      ? "本条填写逐字 initial、final、tone（0 到 1 的小数）。onset、nucleus、coda 填 null。"
      : "本条填写逐字 onset、nucleus、coda（0 到 1 的小数）。initial、final、tone 填 null。",
    reading
      ? "语句 accuracy、completeness、fluency、prosodic 只能是 0、0.25、0.5、0.75、1。"
      : "语句 intelligibility、rhythm 只能是 0、0.25、0.5、0.75、1。",
    "参考文本：",
    input.text,
    "逐字：",
    canonicalJson(chars),
  ];
  if (input.asrText && input.asrText.trim()) {
    lines.push("识别提示（不是标准答案，仅供参考）：", input.asrText.trim());
  }
  if (input.retry) {
    lines.push(
      "上一轮输出无效（为空或不是合法的评分 JSON）。请只输出一个 JSON 对象，不要使用 Markdown，不要输出解释；chars 里每个 char_index 恰好出现一次。",
    );
  }
  return lines.join("\n");
}

const unit = z.number().finite().min(0).max(1);

export const aiModelOutputSchema = z.object({
  chars: z
    .array(
      z.object({
        char_index: z.number().int().nonnegative(),
        initial: unit.nullable().optional(),
        final: unit.nullable().optional(),
        tone: unit.nullable().optional(),
        onset: unit.nullable().optional(),
        nucleus: unit.nullable().optional(),
        coda: unit.nullable().optional(),
        mark_type: z.enum(MARK_TYPES),
        note: z.string().max(200).optional(),
      }),
    )
    .max(400)
    .default([]),
  utterance: z.object({
    accuracy: unit.optional(),
    completeness: unit.optional(),
    fluency: unit.optional(),
    prosodic: unit.optional(),
    intelligibility: unit.optional(),
    rhythm: unit.optional(),
  }),
  summary: z.string().min(1).max(400),
});

export type AiModelOutput = z.infer<typeof aiModelOutputSchema>;

export class AiOutputError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AiOutputError";
  }
}

export function extractJsonObject(text: string): unknown {
  const trimmed = text.trim();
  const fenced = /^```(?:json)?\s*([\s\S]*?)\s*```$/i.exec(trimmed);
  const body = (fenced?.[1] ?? trimmed).trim();
  try {
    return JSON.parse(body) as unknown;
  } catch {
    const start = body.indexOf("{");
    const end = body.lastIndexOf("}");
    if (start >= 0 && end > start) {
      try {
        return JSON.parse(body.slice(start, end + 1)) as unknown;
      } catch {
        throw new AiOutputError("模型输出不是合法 JSON");
      }
    }
    throw new AiOutputError("模型输出不是合法 JSON");
  }
}

export interface AiCharScore {
  char_index: number;
  char: string;
  initial: number | null;
  final: number | null;
  tone: number | null;
  onset: number | null;
  nucleus: number | null;
  coda: number | null;
  mark_type: MarkType;
  note: string;
}

export interface AiDimScore {
  key: string;
  label: string;
  value: number;
  anchor: string;
}

export interface ScoredAssessment {
  overall: number;
  summary: string;
  dims: AiDimScore[];
  chars: AiCharScore[];
}

function dimLabel(domain: "reading" | "singing", key: string): string {
  const row = RUBRIC_DIMENSIONS[domain].find((d) => d[0] === key);
  return row ? row[1] : key;
}

/** A finite score in \[0, 1\], or null when the model left the dimension out. */
function labeledUnit(value: number | null | undefined): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0 || value > 1) return null;
  return value;
}

/**
 * Same sentence split the recorder uses for a passage (`。！？；`).
 * One recording of a segmented task is one of these sentences.
 */
export function splitPassageSentences(text: string): string[] {
  return text
    .split(/(?<=[。！？；])/u)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/** Hand each sentence as many `chars` as it has Han characters, in order. */
export function charsForSentences<T extends { char: string }>(
  chars: readonly T[],
  sentences: readonly string[],
): T[][] {
  let cursor = 0;
  return sentences.map((sentence) => {
    const n = sentence.match(/\p{Script=Han}/gu)?.length ?? 0;
    const slice = chars.slice(cursor, cursor + n);
    cursor += n;
    return [...slice];
  });
}

/**
 * Reference for one recording.
 *
 * T4 is one sentence per take (`segment_index`). Any other task is scored
 * the same way when the recording carries a `segment_index` and the text
 * splits into more than one sentence. Everything else keeps the whole text.
 */
export function selectAiReference<T extends Pick<MaterialChar, "char">>(input: {
  task: string;
  text: string;
  chars: readonly T[];
  segmentIndex: number | null;
}): { text: string; chars: T[] } {
  const sentences = splitPassageSentences(input.text);
  const segmented =
    sentences.length > 1 && (input.task === "T4_passage" || input.segmentIndex !== null);
  if (!segmented) return { text: input.text, chars: [...input.chars] };
  const index = input.segmentIndex;
  if (index === null || !Number.isInteger(index) || index < 0 || index >= sentences.length) {
    throw new AiOutputError("录音缺少有效的句子序号");
  }
  const sentence = sentences[index] ?? "";
  const chars = charsForSentences(input.chars, sentences)[index] ?? [];
  if (!sentence || chars.length === 0) throw new AiOutputError("这一句没有可评分的字");
  return { text: sentence, chars };
}

/**
 * Validate model JSON against the material and compute the overall score.
 *
 * Per character: the domain's rubric weights (`charScore`).
 * Utterance: rubric weights, each dimension snapped to an anchor first, then
 * divided by the weight sum so a perfect reading (weights total 0.85) is 1.
 * Overall: the mean of the character mean and the utterance score.
 */
function readingCharScore(
  initial: number | null,
  final: number,
  tone: number,
  zeroInitial: boolean,
): number {
  if (zeroInitial) {
    const weight = READING_WEIGHTS.final + READING_WEIGHTS.tone;
    return round4((READING_WEIGHTS.final * final + READING_WEIGHTS.tone * tone) / weight);
  }
  return charScore(initial ?? 0, final, tone, "reading") ?? 0;
}

export function scoreAssessment(
  raw: unknown,
  domain: Domain,
  materialChars: readonly Pick<MaterialChar, "char" | "initial">[],
): ScoredAssessment {
  const parsed = aiModelOutputSchema.safeParse(raw);
  if (!parsed.success) throw new AiOutputError("模型输出不符合评分 JSON");
  const singing = domain === "singing";
  const byIndex = new Map<number, (typeof parsed.data.chars)[number]>();
  for (const row of parsed.data.chars) {
    if (row.char_index >= materialChars.length) continue;
    if (byIndex.has(row.char_index)) throw new AiOutputError("逐字结果有重复的 char_index");
    byIndex.set(row.char_index, row);
  }

  const chars: AiCharScore[] = [];
  const charScores: number[] = [];
  for (let i = 0; i < materialChars.length; i++) {
    const row = byIndex.get(i);
    const glyph = materialChars[i];
    if (!glyph) throw new AiOutputError("逐字结果与参考文本对不齐");
    const note = (row?.note ?? "").trim().slice(0, 80);
    const mark = row?.mark_type ?? "unrecognizable";
    if (singing) {
      const onset = labeledUnit(row?.onset);
      const nucleus = labeledUnit(row?.nucleus);
      const coda = labeledUnit(row?.coda);
      const missing = onset === null && nucleus === null && coda === null;
      const score = missing ? 0 : (charScore(onset ?? 0, nucleus ?? 0, coda ?? 0, "singing") ?? 0);
      charScores.push(score);
      chars.push({
        char_index: i,
        char: glyph.char,
        initial: null,
        final: null,
        tone: null,
        onset: onset ?? 0,
        nucleus: nucleus ?? 0,
        coda: coda ?? 0,
        mark_type: row ? mark : "unrecognizable",
        note,
      });
    } else {
      // `initial === null` on the material is a zero-initial syllable (迎、一、啊).
      // A missing model initial is valid there. Any other omitted score is 0.
      const zeroInitial = glyph.initial === null;
      const initial = labeledUnit(row?.initial);
      const final = labeledUnit(row?.final);
      const tone = labeledUnit(row?.tone);
      const missing = final === null && tone === null && (zeroInitial || initial === null);
      const score = missing
        ? 0
        : readingCharScore(zeroInitial ? null : (initial ?? 0), final ?? 0, tone ?? 0, zeroInitial);
      charScores.push(score);
      chars.push({
        char_index: i,
        char: glyph.char,
        initial: zeroInitial ? null : (initial ?? 0),
        final: final ?? 0,
        tone: tone ?? 0,
        onset: null,
        nucleus: null,
        coda: null,
        mark_type: row ? mark : "unrecognizable",
        note,
      });
    }
  }

  const utterKeys = singing ? SINGING_UTTERANCE_KEYS : READING_UTTERANCE_KEYS;
  const weights = singing ? RUBRIC_WEIGHTS.singingUtterance : RUBRIC_WEIGHTS.readingUtterance;
  const snapped: Record<string, number> = {};
  const dims: AiDimScore[] = [];
  for (const key of utterKeys) {
    const value = parsed.data.utterance[key];
    if (typeof value !== "number") throw new AiOutputError(`缺少语句维度 ${key}`);
    const anchor = snapToAnchor(value);
    snapped[key] = anchor;
    dims.push({
      key,
      label: dimLabel(singing ? "singing" : "reading", key),
      value: anchor,
      anchor: anchorLabel(anchor),
    });
  }
  let weighted = 0;
  let weightSum = 0;
  for (const key of utterKeys) {
    const w = weights[key as keyof typeof weights];
    weighted += w * (snapped[key] ?? 0);
    weightSum += w;
  }
  if (weightSum <= 0 || charScores.length === 0) throw new AiOutputError("无法计算总分");
  const utterance = weighted / weightSum;
  const meanChar = charScores.reduce((sum, n) => sum + n, 0) / charScores.length;
  const summary = parsed.data.summary.trim().slice(0, 200);
  if (!summary) throw new AiOutputError("缺少总评");
  return {
    overall: round4((meanChar + utterance) / 2),
    summary,
    dims,
    chars,
  };
}

export interface AiUsage {
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  total_tokens: number;
}

export function emptyUsage(): AiUsage {
  return {
    input_tokens: 0,
    output_tokens: 0,
    cache_read_tokens: 0,
    cache_write_tokens: 0,
    total_tokens: 0,
  };
}

export function addUsage(a: AiUsage, b: AiUsage): AiUsage {
  return {
    input_tokens: a.input_tokens + b.input_tokens,
    output_tokens: a.output_tokens + b.output_tokens,
    cache_read_tokens: a.cache_read_tokens + b.cache_read_tokens,
    cache_write_tokens: a.cache_write_tokens + b.cache_write_tokens,
    total_tokens: a.total_tokens + b.total_tokens,
  };
}

/** True when any token counter is non-zero, not only `total_tokens`. */
export function hasUsage(usage: AiUsage | null | undefined): boolean {
  if (!usage) return false;
  return (
    usage.input_tokens > 0 ||
    usage.output_tokens > 0 ||
    usage.cache_read_tokens > 0 ||
    usage.cache_write_tokens > 0 ||
    usage.total_tokens > 0
  );
}

/** cache_read / (cache_read + uncached input). Null when nothing was billed. */
export function cacheHitRate(
  usage: Pick<AiUsage, "input_tokens" | "cache_read_tokens">,
): number | null {
  const den = usage.cache_read_tokens + usage.input_tokens;
  if (den <= 0) return null;
  return round4(Math.min(1, usage.cache_read_tokens / den));
}

function num(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return null;
  return value;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/** DSH service-completion-v1 usage. `input_tokens` is the uncached portion. */
export function usageFromDsh(raw: unknown): AiUsage {
  const u = asRecord(raw) ?? {};
  return {
    input_tokens: num(u.input_tokens) ?? 0,
    output_tokens: num(u.output_tokens) ?? 0,
    cache_read_tokens: num(u.cache_read_tokens) ?? 0,
    cache_write_tokens: num(u.cache_write_tokens) ?? 0,
    total_tokens: num(u.total_tokens) ?? 0,
  };
}

/**
 * OpenAI-compatible usage. Cached prompt tokens are the hit; the uncached
 * input is `prompt_tokens - cached`, which is what the hit-rate formula needs.
 */
export function usageFromMimo(raw: unknown): AiUsage {
  const u = asRecord(raw) ?? {};
  const details = asRecord(u.prompt_tokens_details) ?? asRecord(u.input_tokens_details) ?? {};
  const prompt = num(u.prompt_tokens) ?? num(u.input_tokens) ?? 0;
  const cached =
    num(details.cached_tokens) ?? num(u.cached_tokens) ?? num(u.cache_read_tokens) ?? 0;
  const cacheWrite = num(details.cache_write_tokens) ?? num(u.cache_write_tokens) ?? 0;
  const uncached = Math.max(0, prompt - cached);
  return {
    input_tokens: uncached,
    output_tokens: num(u.completion_tokens) ?? num(u.output_tokens) ?? 0,
    cache_read_tokens: cached,
    cache_write_tokens: cacheWrite,
    total_tokens: num(u.total_tokens) ?? prompt + (num(u.completion_tokens) ?? 0),
  };
}

export interface AiPublicResult {
  version: typeof AI_VERSION;
  status: AiStatus;
  model: string | null;
  prompt_version: string | null;
  overall: number | null;
  dims: AiDimScore[];
  chars: AiCharScore[];
  summary: string | null;
  error: string | null;
  notice: typeof AI_NOTICE;
}

export interface AiCacheStats {
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  hit_rate: number | null;
}

export interface AiFailedJob {
  recording_id: string;
  error: string | null;
  error_type: AiErrorType | null;
  http_status: number | null;
  attempts: number;
  updated_at: string;
  model: string | null;
}

export interface AiAdminStatus {
  enabled: true;
  provider: AiAudioProvider;
  model: string;
  asr: boolean;
  backfill: boolean;
  concurrency: number;
  gateway_configured: boolean;
  mimo_configured: boolean;
  gateway_ready: boolean | null;
  daily: { day: string; used: number; limit: number };
  counts: Record<AiStatus, number>;
  cache: AiCacheStats;
  cache_today: AiCacheStats;
  failed: AiFailedJob[];
}
